import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MenuItem } from 'primeng/api';
import { Button } from 'primeng/button';
import { Menu } from 'primeng/menu';
import { DialogService } from 'primeng/dynamicdialog';
import { BdExportAsFileData } from '@bindoc-projects/util-domain-lib';
import { BdCurrentUserService } from '@bindoc-projects/user-lib/data';
import {
  BD_EXPORT_AS_FILE_DEFAULT_CONFIG,
  BdExportAsFileConfig,
} from '../export.config';
import { openBdExportAsFileDialog } from '../open-export-dialog';
import { bdExportAsFileTypesLabel } from '../export-feedback';
import { BD_DIALOG_SERVICE_PROVIDER } from '@bindoc-projects/ui-lib/dialog/ui';

/**
 * The chart-side contract this menu drives. `bd-chart-canvas` implements it;
 * feature components wrapping a canvas can implement it too by delegating.
 * Kept structural (instead of importing the component type) so this lib does
 * not depend on `@bindoc/charts`.
 */
export interface BdChartExportMenuTarget {
  exportAsImage(): Promise<void>;
  openAdvancedExportDialog(): Promise<void>;
}

/**
 * Combined export menu for charts: offers the chart image export (and its
 * advanced options) together with an optional table (CSV/Excel) export.
 *
 * Replaces the floating export button that used to overlay the top-right
 * corner of `bd-chart-canvas` and could cover data points. Place this menu
 * outside the plot area — e.g. in a header row above the chart or inside an
 * existing card menu.
 *
 * Chart-free surfaces use it too: set `imageExport` to `false` and it becomes
 * a plain table-export menu, which is also how a table section gains the
 * tool-connect hand-offs via `additionalItems` without a second trigger.
 */
@Component({
  selector: 'bd-chart-export-menu',
  template: `
    <p-button
      icon="pi pi-ellipsis-v"
      [text]="true"
      severity="secondary"
      size="small"
      ariaLabel="Export"
      [pt]="{ root: { 'data-test': dataTestKey() } }"
      (click)="menu.toggle($event)"
    />
    <p-menu #menu [model]="menuItems()" [popup]="true" appendTo="body" />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Button, Menu],
  providers: [BD_DIALOG_SERVICE_PROVIDER],
})
export class BdChartExportMenuComponent {
  /** Chart to export. Optional so hosts can bind it before the chart renders. */
  public readonly chart = input<BdChartExportMenuTarget | undefined>();
  /** When set, a "Als Tabelle exportieren" item opens the export-as-file dialog. */
  public readonly tableData = input<BdExportAsFileData | undefined>();
  public readonly tableFilename = input<string | null>(null);
  public readonly tableConfig = input<BdExportAsFileConfig>(
    BD_EXPORT_AS_FILE_DEFAULT_CONFIG,
  );
  /** Shows the "Erweiterte Exportoptionen" item (advanced image export dialog). */
  public readonly advancedImageExport = input(true);
  /**
   * Offers the image export items at all. Set to `false` on pure table
   * surfaces that reuse this menu without a chart behind it.
   */
  public readonly imageExport = input(true);
  /**
   * Extra items shown in their own labelled section — e.g. the tool-connect
   * "Öffnen mit …" actions from `BdToolConnectService.getMenuItems`. Keeping
   * this a plain `MenuItem[]` input avoids a dependency from this lib onto
   * the tool-connect lib; hosts wire the two together.
   */
  public readonly additionalItems = input<MenuItem[]>([]);
  /** Heading of the {@link additionalItems} section. */
  public readonly additionalItemsLabel = input('Öffnen mit …');
  public readonly dataTestKey = input('chart-export-menu-trigger');

  private readonly _dialogService = inject(DialogService);
  private readonly _currentUserService = inject(BdCurrentUserService);

  private readonly _authenticated = toSignal(
    this._currentUserService.authenticated$,
    { initialValue: false },
  );

  public readonly menuItems = computed<MenuItem[]>(() => {
    const exportItems: MenuItem[] = [
      {
        label: this._tableExportLabel(),
        icon: 'pi pi-table',
        visible: this._tableExportVisible(),
        disabled: !this.tableData(),
        command: () => this._openTableExportDialog(),
      },
      {
        label: 'Bild Export',
        icon: 'pi pi-image',
        visible: this.imageExport(),
        disabled: !this.chart(),
        command: () => this.chart()?.exportAsImage(),
      },
      {
        label: 'Erweiterter Bild Export',
        icon: 'pi pi-cog',
        visible: this.advancedImageExport() && this.imageExport(),
        disabled: !this.chart(),
        command: () => this.chart()?.openAdvancedExportDialog(),
      },
    ];
    const additional = this.additionalItems();
    if (additional.length === 0) {
      return exportItems;
    }
    // With additional items present the menu splits into labelled sections so
    // "export this data" and "open this data elsewhere" stay distinguishable.
    // `p-menu` renders a section label regardless of that section's own
    // `visible` flag, so an all-invisible export section is dropped outright
    // instead of leaving a dangling heading behind.
    const sections: MenuItem[] = [];
    if (exportItems.some((entry) => entry.visible !== false)) {
      sections.push({ label: 'Export', items: exportItems });
    }
    sections.push({ label: this.additionalItemsLabel(), items: additional });
    return sections;
  });

  /** Names the offered file formats so users know what each item produces. */
  private readonly _tableExportLabel = computed(() =>
    bdExportAsFileTypesLabel(this.tableConfig().form),
  );

  private readonly _tableExportVisible = computed(() => {
    if (this.tableData() === undefined) {
      return false;
    }
    // Mirrors the `bd-export-as-file` trigger visibility: with the 'always'
    // strategy the table export is reserved for authenticated users; every
    // other strategy handles anonymous users inside the dialog itself.
    if (
      this.tableConfig().authenticationStrategy === 'always' &&
      !this._authenticated()
    ) {
      return false;
    }
    return true;
  });

  private _openTableExportDialog(): void {
    const data = this.tableData();
    if (!data) {
      return;
    }
    openBdExportAsFileDialog(this._dialogService, {
      data,
      filename: this.tableFilename(),
      config: this.tableConfig(),
      authenticated: this._authenticated(),
    });
  }
}
